import { Control, FormState } from "react-hook-form";

import { Col } from "~/components/Grid";
import { Approver } from "~/application/types";
import { Flex, FlexProps } from "~/components/Flex";
import { FieldLabel, FormControl } from "~/components/FormControl";
import { Icon } from "~/components/Icon/Icon";
import { IconButton } from "~/components/IconButton";
import { TooltipLabel, Tooltip } from "~/components/Tooltip";
import { Caption, H6 } from "~/components/Typography";
import { AsyncSelect } from "../../components/AsyncSelect/AsyncSelect";
import { EMPTY_LEVEL } from "./utils";
import { Level } from "~/application/types";
import { SvgPlus, SvgTrash } from "~/components/Icon/icons";
import { Select, Switch } from "~/components/Input";
import { DataColItem, DataListItem } from "~/components/List";
import { Text } from "~/components/Text";
import { GroupEmployee } from "~/core/modules/Customer/pages/CustomerPage/hooks/useCustomerEmployeesGroups";
import { ApprovalModelSchema, OnSelectApproverParams } from "./types";

export interface ApprovalLevelListItemProps extends FlexProps {
  level: Level;
  index: number;
  defaultApprovers: Approver[];
  control: Control<ApprovalModelSchema>;
  formState: FormState<ApprovalModelSchema>;
  isBrokenPolicyModel: boolean;
  onSelectApprover: (params: OnSelectApproverParams) => void;
  fetchApprovers: (
    name: string,
    options?: { headquartersAccess?: boolean }
  ) => Promise<GroupEmployee[]>;
  onAddLevel: (level: Level, levelIndex: number) => void;
  onRemoveLevel: (index: number) => void;
  onRemoveApprover: (approver: Approver, levelIndex: number) => void;
  isGroupType: boolean;
  onSearchApprover: (name: string) => void;
  isIssuer?: boolean;
}

export const ApprovalLevelListItem = ({
  level,
  index: levelIndex,
  control,
  formState,
  defaultApprovers,
  onSelectApprover,
  fetchApprovers,
  onAddLevel,
  onRemoveLevel,
  onRemoveApprover,
  onSearchApprover,
  isGroupType,
  isBrokenPolicyModel,
  isIssuer = false,
  ...props
}: ApprovalLevelListItemProps) => {
  return (
    <Flex gap="2" direction="column" {...props}>
      <Flex align="center" justify="between">
        <H6>Nível {levelIndex + 1}</H6>

        {!isIssuer && (
          <Flex>
            <Tooltip content={<TooltipLabel>Adicionar nível</TooltipLabel>}>
              <IconButton size="lg" onClick={() => onAddLevel(EMPTY_LEVEL, levelIndex + 1)} data-cy={`add-level-${levelIndex + 1}`}>
                <Icon as={SvgPlus} />
              </IconButton>
            </Tooltip>

            <Tooltip content={<TooltipLabel>Remover nível </TooltipLabel>}>
              <IconButton size="lg" onClick={() => onRemoveLevel(levelIndex)}>
                <Icon as={SvgTrash} />
              </IconButton>
            </Tooltip>
          </Flex>
        )}
      </Flex>
      {isGroupType && !isBrokenPolicyModel && (
        <Select
          onChange={(approver) => onSelectApprover({ approver, levelIndex })}
          dataCy={`approval-model-group-approver-${levelIndex}`}
          onInputChange={onSearchApprover}
          options={defaultApprovers}
          getOptionLabel={(option) => `${option.name ?? ""} ${option.lastName ?? ""}`}
          getOptionValue={(option) => option.uuid}
          placeholder="Selecione o aprovador"
          size="sm"
          disabled={isIssuer}
        />
      )}

      {(!isGroupType || (isGroupType && isBrokenPolicyModel)) && (
        <Select
          dataCy={`approval-model-group-approver-${levelIndex}`}
          onChange={(approver) => onSelectApprover({ approver, levelIndex })}
          options={defaultApprovers}
          onInputChange={onSearchApprover}
          getOptionLabel={(option) => `${option.name ?? ""} ${option.lastName ?? ""}`}
          getOptionValue={(option) => option.uuid}
          placeholder="Selecione o aprovador"
          size="sm"
          disabled={isIssuer}
        />
      )}

      {formState.errors.levels && formState.errors.levels[levelIndex]?.approvers && (
        <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
          {formState.errors.levels[levelIndex as number]!.approvers?.message}
        </Text>
      )}

      <FieldLabel css={{ mt: "$4" }}>Aprovadores</FieldLabel>

      {level.approvers
        .filter((a) => a.uuid)
        .map((approver, approverIndex) => {
          return (
            <DataListItem data-active key={`approver-${approverIndex}`}>
              <Col>
                <FormControl
                  control={control}
                  name={`levels.${levelIndex}.approvers.${approverIndex}.name`}
                >
                  <DataColItem
                    headerTitle="Aprovador"
                    boxCss={{ mb: "$2", mt: "$4" }}
                    data={`${approver.name ?? ""} ${approver.lastName ?? ""}`}
                  />
                </FormControl>
              </Col>

              <Col>
                <FormControl
                  name={`levels.${levelIndex}.approvers.${approverIndex}.selfApprover`}
                  control={control}
                >
                  <Flex justify="end" css={{ width: "100%" }}>
                    <Col sz="5">
                      <DataColItem
                        headerTitle="Aprova a si mesmo"
                        boxCss={{ mt: "$2" }}
                        data={
                          <Switch checked={approver.selfApprover} disabled={isIssuer} data-cy={`approval-model-self-approver-${levelIndex}-${approverIndex}`}>
                            <Caption>{approver.selfApprover ? "Sim" : "Não"}</Caption>
                          </Switch>
                        }
                      />
                    </Col>

                    {!isIssuer && (
                      <Col sz="1">
                        <Tooltip content={<TooltipLabel>Remover aprovador</TooltipLabel>}>
                          <IconButton
                            css={{ mt: "$4" }}
                            size="lg"
                            onClick={() => onRemoveApprover(approver, levelIndex)}
                          >
                            <Icon as={SvgTrash} />
                          </IconButton>
                        </Tooltip>
                      </Col>
                    )}
                  </Flex>
                </FormControl>
              </Col>
            </DataListItem>
          );
        })}
    </Flex>
  );
};
